import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search, UserPlus, X } from 'lucide-react';
import { fetchAllUserDetails } from '../../../../../service/SubscriptionWellDhanService';

/** Same event type as Users list (`/user` → UserListDashboard) */
const USER_LIST_EVENT_TYPE_ID = '4850387a-8838-45d5-8c65-dce9d1f86de6';

function userDisplayName(u) {
  if (!u) return '';
  const fn = u.first_name ?? u.user_first_name ?? '';
  const ln = u.last_name ?? u.user_last_name ?? '';
  return `${fn} ${ln}`.trim() || 'Unknown user';
}

function resolveUserId(u) {
  if (!u) return null;
  return u.user_id ?? u.id ?? null;
}

function userEmail(u) {
  return (u?.email || u?.user_email || '').trim();
}

function userPhone(u) {
  return (u?.phone_number || u?.user_phone_number || u?.phone || '').trim();
}

function UserDetailBlock({ name, email, phone, selected = false }) {
  return (
    <div className="flex-1 min-w-0 text-sm">
      <span className={`block font-medium ${selected ? 'text-orange-900' : 'text-gray-900'}`}>
        {name}
      </span>
      <span className="block text-xs text-gray-600 mt-0.5 truncate">{email || '—'}</span>
      <span className="block text-xs text-gray-600 truncate">{phone || '—'}</span>
    </div>
  );
}

/**
 * Step 1 of copy: choose target user (defaults to current), then continue to plan dates.
 */
export default function CopyWorkoutPlanModal({
  isOpen,
  onClose,
  onContinue,
  currentUserId,
  planName = 'Workout plan',
  sourceLabel = '',
}) {
  const [targetUserId, setTargetUserId] = useState(currentUserId);
  const [targetUser, setTargetUser] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [myUsersOnly, setMyUsersOnly] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const reset = useCallback(() => {
    setTargetUserId(currentUserId);
    setTargetUser(null);
    setSearchInput('');
    setDebouncedSearch('');
    setSearchResults([]);
    setSearchLoading(false);
    setMyUsersOnly(true);
  }, [currentUserId]);

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!isOpen) return;
    if (debouncedSearch.length < 2) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setSearchLoading(true);
      try {
        const res = await fetchAllUserDetails(
          USER_LIST_EVENT_TYPE_ID,
          20,
          0,
          debouncedSearch,
          'user_joining_date',
          'desc',
          null,
          myUsersOnly
        );
        if (cancelled) return;
        setSearchResults(res.success && Array.isArray(res.data?.users) ? res.data.users : []);
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, isOpen, myUsersOnly]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isSameUser = targetUserId != null && String(targetUserId) === String(currentUserId);
  const hasSelection = targetUserId != null && targetUserId !== '';
  const targetUserName = isSameUser
    ? 'This user'
    : targetUser
      ? userDisplayName(targetUser)
      : '';

  const selectSameUser = (checked) => {
    if (checked) {
      setTargetUserId(currentUserId);
      setTargetUser(null);
    } else {
      setTargetUserId(null);
      setTargetUser(null);
    }
  };

  const toggleSearchUser = (u, checked) => {
    const id = resolveUserId(u);
    if (!id) return;
    if (checked) {
      setTargetUserId(id);
      setTargetUser(u);
    } else if (String(targetUserId) === String(id)) {
      setTargetUserId(null);
      setTargetUser(null);
    }
  };

  const handleContinue = () => {
    if (!targetUserId) return;
    onContinue({
      targetUserId,
      targetUserName: targetUserName || 'Selected user',
      isSameUser,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="copy-workout-plan-title"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 bg-orange-100 rounded-full shrink-0">
              <UserPlus className="w-5 h-5 text-orange-600" />
            </div>
            <div className="min-w-0">
              <h3 id="copy-workout-plan-title" className="text-lg font-bold text-gray-900">
                Copy plan
              </h3>
              <p className="text-sm text-gray-500 mt-0.5 truncate" title={planName}>
                {planName}
                {sourceLabel ? ` · ${sourceLabel}` : ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Choose who receives this plan. Default is the current profile; search to copy to
            someone else. Uncheck to clear the selection.
          </p>

          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={myUsersOnly}
              onChange={(e) => setMyUsersOnly(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span>Only my assigned users</span>
          </label>

          <div>
            <p className="text-sm font-medium text-gray-800 mb-2">1. Choose user</p>
            <label
              className={`flex items-start gap-3 w-full cursor-pointer rounded-lg border px-3 py-2.5 transition-colors ${
                isSameUser
                  ? 'border-orange-primary bg-orange-50'
                  : 'border-gray-200 hover:border-orange-200'
              }`}
            >
              <input
                type="checkbox"
                className="mt-1 rounded border-gray-300"
                checked={isSameUser}
                onChange={(e) => selectSameUser(e.target.checked)}
              />
              <span className="text-sm">
                <span className={`block font-medium ${isSameUser ? 'text-orange-900' : 'text-gray-900'}`}>
                  This user
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">Same profile</span>
              </span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Find users</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Type name (2+ characters)…"
                className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-primary focus:outline-none"
              />
            </div>
            {searchLoading && (
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Searching…
              </p>
            )}

            {debouncedSearch.length >= 2 && searchResults.length > 0 && (
              <ul className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                {searchResults.map((u, idx) => {
                  const id = resolveUserId(u);
                  const selected = id != null && String(id) === String(targetUserId);
                  const name = userDisplayName(u);
                  return (
                    <li key={id ? String(id) : `row-${idx}`}>
                      <label
                        className={`flex items-start gap-3 w-full cursor-pointer px-3 py-2.5 transition-colors ${
                          selected ? 'bg-orange-50' : 'hover:bg-gray-50'
                        } ${!id ? 'opacity-40 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 rounded border-gray-300"
                          checked={selected}
                          disabled={!id}
                          onChange={(e) => toggleSearchUser(u, e.target.checked)}
                        />
                        <UserDetailBlock
                          name={name}
                          email={userEmail(u)}
                          phone={userPhone(u)}
                          selected={selected}
                        />
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}

            {debouncedSearch.length >= 2 && !searchLoading && searchResults.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">No users found.</p>
            )}
            {debouncedSearch.length > 0 && debouncedSearch.length < 2 && (
              <p className="text-xs text-gray-400 mt-1">Type at least 2 characters to search</p>
            )}
          </div>

          {!isSameUser && targetUser && (
            <div className="rounded-lg border border-orange-200 bg-orange-50/60 px-3 py-2.5">
              <p className="text-xs font-medium text-orange-900 mb-1">Copying to</p>
              <p className="text-sm font-semibold text-gray-900">{userDisplayName(targetUser)}</p>
              <p className="text-xs text-gray-600 mt-0.5">{userEmail(targetUser) || '—'}</p>
              <p className="text-xs text-gray-600">{userPhone(targetUser) || '—'}</p>
            </div>
          )}

          {!hasSelection && (
            <p className="text-xs text-amber-700">Select a user to continue.</p>
          )}

          {hasSelection && (
            <p className="text-xs text-gray-500">
              Next: choose dates and weekdays for the new plan
              {!isSameUser && targetUserName ? ` for ${targetUserName}` : ''}.
            </p>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!hasSelection}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-orange-primary hover:bg-orange-600 rounded-lg disabled:opacity-50"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
